import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { PlayerAvatar, PlayerTag } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { useGameTimer } from "../gameTimer";

// Hémicycle : un joueur voit une cible sur un demi-cercle entre deux extrêmes
// et donne un indice, les autres placent leur aiguille.
//
// Angles en degrés, 0 à l'extrême gauche, 180 à l'extrême droit. Le serveur
// ne donne la cible et les aiguilles des autres qu'à celui qui a donné
// l'indice, puis à tous à la révélation.
const VB = { w: 460, h: 250 };
const C = { x: 230, y: 228 };
const R = 196;
const MOVE_MS = 80;   // une position envoyée au plus toutes les 80 ms

const pt = (a, r = R) => {
  const rad = (a * Math.PI) / 180;
  return [C.x - r * Math.cos(rad), C.y - r * Math.sin(rad)];
};

function wedge(a1, a2) {
  const [x1, y1] = pt(Math.max(0, a1));
  const [x2, y2] = pt(Math.min(180, a2));
  return `M ${C.x} ${C.y} L ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2} Z`;
}
const FACE = `M ${C.x - R} ${C.y} A ${R} ${R} 0 0 1 ${C.x + R} ${C.y} Z`;

function HemicycleGame({ gameId, player }) {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [choice, setChoice] = useState(0);
  const [clue, setClue] = useState("");
  const [angle, setAngle] = useState(null);   // ma propre aiguille, null tant que je n'ai pas bougé
  const svgRef = useRef(null);
  const dragging = useRef(false);
  const lastSent = useRef(0);
  const { playPop } = useSounds();

  const myId = player?.playerId;

  useEffect(() => {
    socket.on("hmState", setState);
    socket.on("hmGuesses", ({ guesses }) => setState(s => s ? { ...s, guesses } : s));
    socket.on("hmPlayers", ({ players }) => setState(s => s ? { ...s, players } : s));
    socket.emit("hmGetState", { gameId });
    return () => ["hmState", "hmGuesses", "hmPlayers"].forEach(e => socket.off(e));
  }, [gameId]);

  // Nouveau tour : aiguille et indice vierges (ou l'aiguille déjà posée, après
  // une reconnexion).
  const turnKey = state ? `${state.round}-${state.giverId}` : "";
  useEffect(() => {
    setAngle(state?.myGuess ?? null); setClue(""); setChoice(0);
  }, [turnKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (state?.phase === "reveal") playPop(); }, [state?.phase, playPop]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  const phase = state?.phase;
  const isGiver = state?.giverId === myId;
  const me = state?.players?.find(p => p.playerId === myId);
  const canDrag = phase === "guess" && !isGiver && !me?.locked;
  const left = state?.endsAt ? Math.max(0, Math.ceil((state.endsAt - now) / 1000)) : 0;
  useGameTimer(state ? { remaining: left, duration: state.duration, idle: phase === "reveal" } : null);
  useCountdownTick(left, { active: canDrag });

  if (!state) return <div className="Hemicycle"><p className="pi-waiting">{t("hemicycle.waiting")}</p></div>;

  const giver = state.players.find(p => p.playerId === state.giverId);
  const pair = phase === "clue" && isGiver ? state.choices?.[choice] : state.pair;
  // La cible se voit côté indice et à la révélation ; le reste du temps, la
  // face est pleine et l'on ne voit que sa propre aiguille.
  const showTarget = state.target != null;
  const w = state.zoneWidth;

  // ── Aiguille ───────────────────────────────────────────────────────────────
  const angleAt = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) * (VB.w / r.width) - C.x;
    const y = C.y - (e.clientY - r.top) * (VB.h / r.height);
    const deg = 180 - (Math.atan2(Math.max(0, y), x) * 180) / Math.PI;
    return Math.max(0, Math.min(180, deg));
  };
  const move = (e, force) => {
    const a = angleAt(e);
    setAngle(a);
    if (force || Date.now() - lastSent.current >= MOVE_MS) {
      lastSent.current = Date.now();
      socket.emit("hmMove", { gameId, angle: a });
    }
  };
  const onDown = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    move(e, true);
  };
  const onMove = (e) => { if (dragging.current) move(e); };
  const onUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    move(e, true);
  };

  // Révélation et vue de l'indice : l'aiguille montre la cible ; sinon la mienne.
  const needle = phase === "reveal" || (isGiver && showTarget) ? state.target : (angle ?? 90);
  const [nx, ny] = pt(needle, R * 0.82);

  // Avatars autour de l'arc, décalés vers l'extérieur quand ils se chevauchent
  const markers = [];
  if (state.guesses) {
    const sorted = Object.entries(state.guesses).sort((a, b) => a[1] - b[1]);
    let prev = -99, level = 0;
    for (const [pid, a] of sorted) {
      const p = state.players.find(x => x.playerId === pid);
      if (!p) continue;
      level = a - prev < 9 ? level + 1 : 0;
      prev = a;
      const [x, y] = pt(a, R + 26 + level * 34);
      markers.push({ p, x, y, gain: state.gains?.[pid] });
    }
  }

  const status = phase === "clue"
    ? (isGiver ? t("hemicycle.yourTurn") : t("hemicycle.thinking", { pseudo: giver?.pseudo || "?" }))
    : phase === "guess"
    ? (isGiver ? t("hemicycle.othersPlacing") : me?.locked ? t("hemicycle.locked") : t("hemicycle.dragHint"))
    : state.reason === "noClue" ? t("hemicycle.noClue")
    : null;

  const sendClue = (e) => {
    e.preventDefault();
    if (!clue.trim()) return;
    socket.emit("hmClue", { gameId, index: choice, clue });
  };

  return (
    <div className="Hemicycle">
      <div className="hm-layout">
        <aside className="bt-panel bt-left">
          <ul className="bt-ranking">
            {[...state.players].sort((a, b) => b.score - a.score).map((p, i) => (
              <li key={p.playerId}>
                <PlayerTag player={p} pos={i + 1} score={p.score}
                  className={`${p.playerId === myId ? "me" : ""}${p.offline ? " offline" : ""}${p.giving ? " hm-giving" : ""}${p.locked ? " hm-locked" : ""}`} />
              </li>
            ))}
          </ul>
        </aside>

        <main className="hm-main">
          <p className="hm-kicker">
            {t("hemicycle.of")} <span>{giver?.pseudo}</span>
            <small>{t("pictionary.roundOf", { round: state.round + 1, total: state.totalRounds })}</small>
          </p>
          {state.clue ? <h2 className="hm-clue">{state.clue}</h2> : null}
          {status && <p className="hm-status">{status}</p>}

          {phase === "clue" && isGiver && (
            <div className="hm-pairs">
              {(state.choices || []).map((c, i) => (
                <button key={i} type="button" className={`pi-choice${choice === i ? " on" : ""}`}
                  onClick={() => setChoice(i)}>{c.left} / {c.right}</button>
              ))}
            </div>
          )}

          <div className="hm-gauge">
            <svg ref={svgRef} viewBox={`0 0 ${VB.w} ${VB.h}`} className={canDrag ? "draggable" : ""}
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
              <path d={FACE} className={`hm-face${showTarget ? "" : " hidden"}`} />
              {showTarget && <>
                <path d={wedge(state.target - 2.5 * w, state.target + 2.5 * w)} className="hm-zone-far" />
                <path d={wedge(state.target - 1.5 * w, state.target + 1.5 * w)} className="hm-zone-near" />
                <path d={wedge(state.target - 0.5 * w, state.target + 0.5 * w)} className="hm-zone-center" />
              </>}
              <path d={FACE} className="hm-rim" />
              {/* Révélation : ma propre aiguille reste visible, en fantôme */}
              {phase === "reveal" && !isGiver && angle != null && (() => {
                const [gx, gy] = pt(angle, R * 0.82);
                return <line x1={C.x} y1={C.y} x2={gx} y2={gy} className="hm-needle ghost" />;
              })()}
              {(phase !== "clue" || isGiver) && <>
                <line x1={C.x} y1={C.y} x2={nx} y2={ny} className="hm-needle" />
                <circle cx={C.x} cy={C.y} r="13" className="hm-hub" />
              </>}
            </svg>
            {markers.map(({ p, x, y, gain }) => (
              <div key={p.playerId} className="hm-marker" title={p.pseudo}
                style={{ left: `${(x / VB.w) * 100}%`, top: `${(y / VB.h) * 100}%` }}>
                <PlayerAvatar player={p} size={38} />
                {gain != null && <b className={gain > 0 ? "ok" : ""}>+{gain}</b>}
              </div>
            ))}
          </div>

          {pair && (
            <div className="hm-ends"><span>{pair.left}</span><span>{pair.right}</span></div>
          )}

          {phase === "clue" && isGiver && (
            <form className="bt-input-row hm-clue-form" onSubmit={sendClue}>
              <input value={clue} maxLength={40} autoFocus autoComplete="off"
                placeholder={t("hemicycle.cluePlaceholder")} onChange={e => setClue(e.target.value)} />
              <button className="btn btn-primary" disabled={!clue.trim()}>{t("hemicycle.send")}</button>
            </form>
          )}
          {canDrag && (
            <button className="btn btn-primary hm-lock" disabled={angle == null}
              onClick={() => socket.emit("hmLock", { gameId })}>{t("hemicycle.lock")}</button>
          )}
        </main>
      </div>
    </div>
  );
}

export default HemicycleGame;
